import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AngularMaterialCommonModule } from '@app/shared/angular-material/angular-material-common.module';
import { PlanDataEnum } from '@app/plan/plan.model';
import { PlanService } from '@app/plan/plan.service';
import { StatementService } from '@app/plan/statement/statement.service';

import { MobileBottomNavigationFloatingMenuComponent } from './floating-menu/mobile-bottom-navigation-floating-menu/mobile-bottom-navigation-floating-menu.component';
import { MobileBottomNavigationFloatingProfileComponent } from './floating-menu/mobile-bottom-navigation-floating-profile/mobile-bottom-navigation-floating-profile.component';
import { navigationItemAnimation } from './mobile-bottom-navigation.constants';
import {
  INavigationItem,
  MobileBottomNavigationRoutesEnum,
  NavigationItemIdEnum
} from './mobile-bottom-navigation.model';
import { MobileBottomNavigationService } from './mobile-bottom-navigation.service';

@Component({
  selector: 'app-mobile-bottom-navigation',
  templateUrl: './mobile-bottom-navigation.component.html',
  styleUrl: './mobile-bottom-navigation.component.scss',
  animations: [navigationItemAnimation],
  imports: [
    CommonModule,
    AngularMaterialCommonModule,
    MobileBottomNavigationFloatingMenuComponent,
    MobileBottomNavigationFloatingProfileComponent
  ]
})
export class MobileBottomNavigationComponent implements OnInit, OnDestroy {
  navigationItemIdEnum = NavigationItemIdEnum;
  routeChangesSubscription: Subscription;
  navigationItemIdFromCurrentRoute?: NavigationItemIdEnum;

  items: INavigationItem[] = [
    {
      id: NavigationItemIdEnum.MENU,
      title: 'Menu',
      isActive: false,
      icons: {
        default: 'mobile-bottom-navigation__menu',
        active: 'mobile-bottom-navigation__menu'
      }
    },
    {
      id: NavigationItemIdEnum.SEARCH,
      title: 'Busca',
      isActive: false,
      icons: {
        default: 'mobile-bottom-navigation__search',
        active: 'mobile-bottom-navigation__search-filled'
      }
    },
    {
      id: NavigationItemIdEnum.HOME,
      title: 'Início',
      isActive: false,
      icons: {
        default: 'mobile-bottom-navigation__home',
        active: 'mobile-bottom-navigation__home-filled'
      }
    },
    {
      id: NavigationItemIdEnum.STATEMENT,
      title: 'Extrato',
      isActive: false,
      icons: {
        default: 'mobile-bottom-navigation__statement',
        active: 'mobile-bottom-navigation__statement-filled'
      }
    },
    {
      id: NavigationItemIdEnum.PROFILE,
      title: 'Perfil',
      isActive: false,
      icons: {
        default: 'mobile-bottom-navigation__profile',
        active: 'mobile-bottom-navigation__profile-filled'
      }
    }
  ];

  constructor(
    private mobileBottomNavigationService: MobileBottomNavigationService,
    private statementService: StatementService,
    private planService: PlanService,
    private router: Router
  ) {}

  get isMenuActive() {
    const menu = this.items.find(
      (item) => item.id === NavigationItemIdEnum.MENU
    );
    return menu.isActive;
  }

  get isProfileActive() {
    const profile = this.items.find(
      (item) => item.id === NavigationItemIdEnum.PROFILE
    );
    return profile.isActive;
  }

  ngOnInit(): void {
    this.routeChangesSubscription = this.mobileBottomNavigationService
      .watchRouteChanges$()
      .subscribe((navigationItemId) => {
        this.navigationItemIdFromCurrentRoute = navigationItemId;

        if (navigationItemId) {
          this.setItemsOnRouteChanges(navigationItemId);
        }
      });
  }

  ngOnDestroy(): void {
    this.routeChangesSubscription.unsubscribe();
  }

  handleChangeSelection(navigationItemId: NavigationItemIdEnum) {
    if (navigationItemId === NavigationItemIdEnum.HOME) {
      this.handleHomeClick();
    } else if (navigationItemId === NavigationItemIdEnum.SEARCH) {
      this.handleSearchClick();
    } else if (navigationItemId === NavigationItemIdEnum.STATEMENT) {
      this.handleStatementClick();
    } else if (
      navigationItemId === NavigationItemIdEnum.MENU ||
      navigationItemId === NavigationItemIdEnum.PROFILE
    ) {
      this.handleSubmenuClick(navigationItemId);
    }
  }

  handleHomeClick() {
    this.items = this.items.map((item) => {
      return {
        ...item,
        isActive: item.id === NavigationItemIdEnum.HOME
      };
    });
    const registration = this.planService.getPlanData(
      PlanDataEnum.Registration
    );
    this.router.navigate([
      MobileBottomNavigationRoutesEnum.PLAN_MANAGER,
      registration
    ]);
  }

  handleStatementClick() {
    this.items = this.items.map((item) => {
      return {
        ...item,
        isActive: item.id === NavigationItemIdEnum.STATEMENT
      };
    });

    const { url } = this.router;
    if (url.includes(MobileBottomNavigationRoutesEnum.STATEMENT)) {
      return;
    }

    const registration = this.planService.getPlanData(
      PlanDataEnum.Registration
    );
    this.statementService.navigateToStatement(registration);
  }

  handleSubmenuClick(navigationItemId: NavigationItemIdEnum) {
    const submenu = this.items.find((item) => item.id === navigationItemId);
    const isSubmenuActive = !submenu.isActive;

    this.items = this.items.map((item) => {
      if (item.id === navigationItemId) {
        return {
          ...item,
          isActive: isSubmenuActive
        };
      } else if (
        item.id === this.navigationItemIdFromCurrentRoute &&
        !isSubmenuActive
      ) {
        return {
          ...item,
          isActive: true
        };
      }

      return {
        ...item,
        isActive: false
      };
    });
  }

  handleSearchClick() {
    this.items = this.items.map((item) => {
      return {
        ...item,
        isActive: item.id === NavigationItemIdEnum.SEARCH
      };
    });
    const registration = this.planService.getPlanData(
      PlanDataEnum.Registration
    );
    this.router.navigate([
      MobileBottomNavigationRoutesEnum.SEARCH,
      registration
    ]);
  }

  setItemsOnRouteChanges(currentRouteNavItem: NavigationItemIdEnum) {
    this.items = this.items.map((item) => {
      const resetItem: INavigationItem = {
        ...item,
        isActive: false
      };

      if (item.id === currentRouteNavItem) {
        return {
          ...resetItem,
          isActive: true
        };
      }

      return resetItem;
    });
  }
}
